import { useState, useEffect, useRef } from 'react';
import socket from '../socket';

// Initialize tabId once per browser tab
if (!localStorage.getItem("tabId")) {
  localStorage.setItem("tabId", String(Date.now()));
}

const useDeploymentSocket = () => {
  const [currentStage, setCurrentStage] = useState(0);
  const [statusMessages, setStatusMessages] = useState([]);
  const [errorStage, setErrorStage] = useState(null);

  const currentStageRef = useRef(currentStage);
  const errorStageRef = useRef(errorStage);
  const logEndRef = useRef(null);
  const tabId = useRef(localStorage.getItem("tabId"));
  const totalStagesRef = useRef(7);

  const backend_url = process.env.REACT_APP_BACKEND_URL;

  // Keep refs in sync
  useEffect(() => { currentStageRef.current = currentStage; }, [currentStage]);
  useEffect(() => { errorStageRef.current = errorStage; }, [errorStage]);

  // Auto-scroll logs
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [statusMessages]);

  // Socket listener
  useEffect(() => {
    socket.on("deployment-update", (data) => {
      const ownerTab = localStorage.getItem("activeStarterTab");
      const activeCluster = localStorage.getItem("activeCluster");

      if (ownerTab !== tabId.current) return;
      if (!activeCluster) return;
      if (data.clusterName && data.clusterName !== activeCluster) return;

      // Log
      if (data.log) {
        setStatusMessages(prev => [...prev, data.log]);
        return;
      }

      // Stage starting
      if (data.stage !== undefined && data.starting === true) {
        const stageIndex = data.stage - 1;
        setCurrentStage(stageIndex);
        localStorage.setItem("activeStage", String(stageIndex));
        if (data.message) {
          setStatusMessages(prev => [...prev, data.message]);
        }
        return;
      }

      // Error
      if (data.error === true) {
        const failedStage = data.stage - 1;
        setErrorStage(failedStage);
        errorStageRef.current = failedStage;
        setCurrentStage(failedStage);
        localStorage.setItem("activeStage", String(failedStage));
        setStatusMessages(prev => [...prev, `Error: ${data.message}`]);
        return;
      }

      // After failure, ignore everything except logs
      if (errorStageRef.current !== null) return;

      // Progress (stage completed)
      if (data.stage !== undefined && data.progress === true) {
        const stageIndex = data.stage;
        setCurrentStage(stageIndex);
        localStorage.setItem("activeStage", String(stageIndex));

        if (data.stage === totalStagesRef.current) {
          localStorage.removeItem("activeCluster");
          localStorage.removeItem("activeStarterTab");
          localStorage.removeItem("activeStage");
          localStorage.removeItem("activeDeploymentType");
          localStorage.removeItem("activeEnableHttps");
        }

        if (data.message) {
          setStatusMessages(prev => [...prev, data.message]);
        }
        return;
      }

      // Fallback
      if (data.message) {
        setStatusMessages(prev => [...prev, data.message]);
      }
    });

    return () => socket.off("deployment-update");
  }, []);

  // Resume on mount — restore state from localStorage + sync with backend
  useEffect(() => {
    const saved = localStorage.getItem("activeStage");
    if (saved !== null) {
      setCurrentStage(Number(saved));
      setStatusMessages([]);
    }

    const activeCluster = localStorage.getItem("activeCluster");
    if (activeCluster) {
      const token = localStorage.getItem('authToken');
      fetch(`${backend_url}/api/clusters`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => res.json())
        .then(clusters => {
          const cluster = clusters.find(c => c.name === activeCluster);
          if (cluster && cluster.progress_status === "complete" && cluster.state === "Running") {
            setCurrentStage(totalStagesRef.current);
            setErrorStage(null);
            errorStageRef.current = null;
            localStorage.removeItem("activeCluster");
            localStorage.removeItem("activeStarterTab");
            localStorage.removeItem("activeStage");
            localStorage.removeItem("activeDeploymentType");
            localStorage.removeItem("activeEnableHttps");
          }
        })
        .catch(() => {});
    }
  }, [backend_url]);

  const startDeployment = (clusterName, totalStages = 7) => {
    totalStagesRef.current = totalStages;
    localStorage.setItem("activeCluster", clusterName);
    localStorage.setItem("activeStarterTab", tabId.current);
    localStorage.setItem("activeStage", "0");
    setStatusMessages([]);
    setCurrentStage(0);
    setErrorStage(null);
    errorStageRef.current = null;
  };

  const resetDeployment = () => {
    localStorage.removeItem("activeCluster");
    localStorage.removeItem("activeStarterTab");
    localStorage.removeItem("activeStage");
    localStorage.removeItem("activeDeploymentType");
    localStorage.removeItem("activeEnableHttps");
    setStatusMessages([]);
    setCurrentStage(0);
    setErrorStage(null);
    errorStageRef.current = null;
  };

  const isActive = localStorage.getItem("activeStage") !== null;

  return {
    currentStage,
    errorStage,
    statusMessages,
    logEndRef,
    isActive,
    startDeployment,
    resetDeployment,
  };
};

export default useDeploymentSocket;
